import { Info, LockIcon, UserIcon, UsersIcon } from "@phosphor-icons/react/dist/ssr";
import { countryCodes } from "apps/visits/packages/shared/static/country-codes";
import { Form } from "apps/visits/packages/ui/src/components";
import { Controller, useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { cn } from "@/core/shared/v2/lib/utils";
import { Input } from "@/core/shared/v2/ui/input";
import { Label } from "@/core/shared/v2/ui/label";
import { Select } from "@/core/shared/v2/ui/select";
import type { FormValues } from "../../user-data-form/validation-schema";
import { PeselSection } from "./pesel-section";

type PatientSectionProps = {
  coreRequired: boolean;
  orderType: FormValues["orderType"];
  noPesel?: boolean;
  onOrderTypeChange: (newOrderType: string, formOnChange: (val: string) => void) => void;
  hasLabplus: boolean;
};

export const PatientSection = ({
  coreRequired,
  orderType,
  noPesel,
  onOrderTypeChange,
  hasLabplus,
}: PatientSectionProps) => {
  const { t } = useTranslation();
  const { control, register, errors } = useFormContext<FormValues>();

  return (
    <div className="mb-4 flex flex-col gap-5">
      <div className="flex items-center gap-3 rounded-xl border border-blue-200 bg-blue-50 p-4 text-blue-700 text-sm leading-relaxed">
        <LockIcon size={24} className="shrink-0" />
        <p>{t("VOUCHERS_DISTRIBUTION.USER_DATA_FORM.SAFE_GUARANTE")}</p>
      </div>
      <div>
        <h3 className="font-serif text-2xl">1. {t("VOUCHERS_DISTRIBUTION.USER_DATA_FORM.PATIENT_DATA")}</h3>
        <p className="mt-1 max-w-[720px] text-pretty text-muted-foreground text-sm">
          {t("VOUCHERS_DISTRIBUTION.USER_DATA_FORM.PATIENT_DATA_DESCRIPTION")}
          {hasLabplus && ` ${t("VOUCHERS_DISTRIBUTION.USER_DATA_FORM.PATIENT_DATA_DESCRIPTION_LABPLUS")}`}
        </p>
      </div>
      <Controller
        control={control}
        name="orderType"
        render={({ onChange, value }) => (
          <div className="flex gap-1.5 rounded-lg bg-gray-50 p-1">
            <button
              type="button"
              data-testid="order-type-for-self"
              className={cn(
                "flex flex-1 items-center justify-center gap-2 rounded-lg border-2 border-transparent px-4 py-1.5 text-xs transition-colors",
                value === "for_self"
                  ? "border-dodger-blue-700 bg-white text-foreground"
                  : "text-muted-foreground hover:bg-gray-100",
              )}
              onClick={() => onOrderTypeChange("for_self", onChange)}
            >
              <UserIcon size={14} className="hidden lg:block" />
              {t("VOUCHERS_DISTRIBUTION.USER_DATA_FORM.FOR_SELF")}
            </button>
            <button
              type="button"
              data-testid="order-type-for-someone"
              className={cn(
                "flex flex-1 items-center justify-center gap-2 rounded-lg border-2 border-transparent px-4 py-1.5 text-xs transition-colors",
                value === "for_someone"
                  ? "border-dodger-blue-700 bg-white text-foreground"
                  : "text-foreground hover:bg-gray-100",
              )}
              onClick={() => onOrderTypeChange("for_someone", onChange)}
            >
              <UsersIcon size={15} className="hidden lg:block" />
              {t("VOUCHERS_DISTRIBUTION.USER_DATA_FORM.FOR_SOMEONE")}
            </button>
          </div>
        )}
      />

      {orderType !== "skip" ? (
        <>
          {orderType === "for_someone" && (
            <div className="fade-in slide-in-from-top-2 flex animate-in flex-col gap-5 fill-mode-both duration-300 lg:flex-row">
              <div className="flex flex-1 flex-col gap-2">
                <Label htmlFor="patientFirstName">
                  {t("VOUCHERS_DISTRIBUTION.USER_DATA_FORM.FIRST_NAME")}
                  {coreRequired ? " *" : ""}
                </Label>
                <Input
                  ref={register}
                  name="patientFirstName"
                  required={coreRequired}
                  autoComplete="new-name"
                  aria-invalid={!!errors.patientFirstName?.message}
                />
                {errors.patientFirstName?.message ? (
                  <p className="font-medium text-destructive text-sm">{errors.patientFirstName.message}</p>
                ) : (
                  <p className="text-muted-foreground text-xs">
                    {t("VOUCHERS_DISTRIBUTION.USER_DATA_FORM.FIRST_NAME_HINT")}
                  </p>
                )}
              </div>
              <div className="flex flex-1 flex-col gap-2">
                <Label htmlFor="patientLastName">
                  {t("VOUCHERS_DISTRIBUTION.USER_DATA_FORM.LAST_NAME")}
                  {coreRequired ? " *" : ""}
                </Label>
                <Input
                  ref={register}
                  name="patientLastName"
                  required={coreRequired}
                  autoComplete="new-name"
                  aria-invalid={!!errors.patientLastName?.message}
                />
                {errors.patientLastName?.message && (
                  <p className="font-medium text-destructive text-sm">{errors.patientLastName.message}</p>
                )}
              </div>
            </div>
          )}

          <PeselSection coreRequired={coreRequired} noPesel={noPesel} orderType={orderType} />

          {orderType === "for_someone" && (
            <div className="fade-in slide-in-from-top-2 mt-4 flex animate-in gap-3 fill-mode-both duration-300">
              <Form.Control className="w-36">
                <div className="flex flex-col gap-2">
                  <Label htmlFor="patientDialCode">{t("VOUCHERS_DISTRIBUTION.USER_DATA_FORM.COUNTRY")}</Label>
                  <Select
                    defaultValue="+48"
                    ref={register}
                    name="patientDialCode"
                    aria-invalid={!!errors.patientDialCode?.message}
                  >
                    {countryCodes.map((country) => (
                      <Select.Option value={country.dial_code.replace(/ /g, "")} key={country.code}>
                        ({country.dial_code})
                      </Select.Option>
                    ))}
                  </Select>
                </div>
              </Form.Control>
              <div className="w-full">
                <div className="flex flex-col gap-2">
                  <Label htmlFor="patientPhone">{t("VOUCHERS_DISTRIBUTION.USER_DATA_FORM.PHONE")}</Label>
                  <Controller
                    control={control}
                    name="patientPhone"
                    render={({ onChange, onBlur, value, ref }) => (
                      <Input
                        name="patientPhone"
                        ref={ref}
                        value={value}
                        onChange={onChange}
                        onBlur={() => {
                          onChange(value?.trim());
                          onBlur();
                        }}
                        inputMode="numeric"
                        aria-invalid={!!errors.patientPhone?.message}
                      />
                    )}
                  />
                </div>
                {errors.patientPhone?.message && (
                  <p className="mt-2 font-medium text-destructive text-sm">{errors.patientPhone.message}</p>
                )}
              </div>
            </div>
          )}
        </>
      ) : (
        <div className="fade-in slide-in-from-top-2 flex animate-in items-start gap-3 rounded-xl bg-blue-50 fill-mode-both p-4 text-blue-800 text-sm leading-relaxed duration-300">
          <Info size={20} weight="fill" className="mt-0.5 shrink-0 text-blue-600" />
          <p>{t("VOUCHERS_DISTRIBUTION.USER_DATA_FORM.SKIP_INFO")}</p>
        </div>
      )}
    </div>
  );
};
